/**
 * Screen 11 — Withdraw winnings.
 *
 * Wireframe pattern: big amount up top + selectable bank-account card +
 * "you receive ₦X" teal note + gold confirm. The /wallet/withdraw/quote
 * endpoint gives the fee + net so users see the exact payout before tapping.
 */

import { api, ApiError } from '../api.js';
import { bindSubmit, escape, setStatus, statusZone } from '../components/form.js';
import { icon, I } from '../components/icons.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatKobo, nairaToKobo } from '../wallet.js';

const STATUS_ID = 'withdraw-status';

export async function renderWithdraw(root) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/wallet" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Withdraw</div>
    </div>
    <div class="pad" id="withdraw-page">
      <div class="skel"></div>
      <div class="skel"></div>
    </div>
  `;

  const host = document.getElementById('withdraw-page');
  let wallet, banks;
  try {
    [wallet, banks] = await Promise.all([
      api('/wallet'),
      api('/wallet/banks'),
    ]);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) { navigate('/login'); return; }
    toast.fromError(err);
    host.innerHTML = `<div class="card" style="color:var(--pink-t)">${escape(err.message)}</div>`;
    return;
  }

  const verified = banks.filter((b) => b.verified);
  if (verified.length === 0) {
    host.innerHTML = `
      <div class="card">
        <b style="font-family:var(--font-display);font-size:14px;color:#fff;font-weight:600;">
          No verified bank accounts
        </b>
        <p class="muted" style="font-size:12.5px;margin:8px 0 14px;">
          Add and verify a bank account before withdrawing.
        </p>
        <a href="/wallet/banks" data-link class="btn">Add a bank account</a>
      </div>
    `;
    return;
  }

  host.innerHTML = `
    <div class="center" style="margin-top:8px;color:var(--mute);font-size:11px;font-weight:600;letter-spacing:0.5px;">
      AMOUNT
    </div>
    <div class="center big" id="amount-display" style="font-size:38px;margin-top:4px;color:#fff;">₦0</div>
    <div class="center" style="font-size:12px;color:var(--mute);font-weight:500;">
      of ${escape(formatKobo(wallet.balance_kobo))} available
    </div>

    <form id="withdraw-form" class="form" novalidate>
      <label class="field" style="margin-top:18px;">
        <span class="field-label">Amount (NGN)</span>
        <input class="field-input" name="amount_naira" type="number" required min="500" step="1" placeholder="e.g. 5000" />
      </label>

      <h3 class="sec">To account</h3>
      <div id="bank-list">
        ${verified.map((b, i) => bankRow(b, i === 0)).join('')}
        <a href="/wallet/banks" data-link class="opt dashed" style="justify-content:center;color:var(--violet);text-decoration:none;">
          + Add another account
        </a>
      </div>
      <input type="hidden" name="bank_account_id" value="${escape(verified[0].id)}" />

      <div class="note teal" id="quote-note">
        Enter an amount to see the fee + payout.
      </div>

      ${statusZone(STATUS_ID)}
      <button type="submit" class="btn gold" disabled id="confirm-btn">Withdraw ₦0</button>
    </form>
  `;

  const form = document.getElementById('withdraw-form');
  const input = form.querySelector('input[name="amount_naira"]');
  const display = document.getElementById('amount-display');
  const quoteNote = document.getElementById('quote-note');
  const confirmBtn = document.getElementById('confirm-btn');
  const bankInput = form.querySelector('input[name="bank_account_id"]');

  // Pick a different bank on tap.
  document.getElementById('bank-list').addEventListener('click', (e) => {
    const card = e.target.closest('[data-bank-id]');
    if (!card) return;
    bankInput.value = card.dataset.bankId;
    document.querySelectorAll('[data-bank-id]').forEach((c) => {
      const on = c === card;
      c.querySelector('.radio')?.classList.toggle('on', on);
    });
  });

  // Debounced quote.
  let quoteToken = 0;
  let debounce;
  async function refreshQuote() {
    const amountNaira = parseInt(input.value || '0', 10) || 0;
    display.textContent = `₦${amountNaira.toLocaleString('en-NG')}`;
    const amountKobo = nairaToKobo(amountNaira);
    if (!Number.isFinite(amountKobo) || amountKobo <= 0 || amountKobo % 100 !== 0) {
      quoteNote.textContent = 'Enter a whole-naira amount.';
      confirmBtn.disabled = true;
      confirmBtn.textContent = 'Withdraw ₦0';
      return;
    }
    const token = ++quoteToken;
    try {
      const q = await api(`/wallet/withdraw/quote?amount_kobo=${amountKobo}`);
      if (token !== quoteToken) return;
      quoteNote.textContent =
        `Paid within 30 min · Fee ${formatKobo(q.fee_kobo)} · You receive ${formatKobo(q.net_kobo)}`;
      confirmBtn.disabled = amountKobo > wallet.balance_kobo;
      confirmBtn.textContent = `Withdraw ${formatKobo(amountKobo)}`;
    } catch (err) {
      if (token !== quoteToken) return;
      quoteNote.textContent = err.message || 'Could not quote a fee.';
      confirmBtn.disabled = true;
    }
  }
  input.addEventListener('input', () => {
    clearTimeout(debounce);
    debounce = setTimeout(refreshQuote, 250);
  });

  const idempotencyKey = `wd-${crypto.randomUUID()}`;

  bindSubmit(form, async (data) => {
    setStatus(STATUS_ID, { message: 'Submitting…', kind: 'info' });
    const amountKobo = nairaToKobo(data.amount_naira);
    if (!Number.isFinite(amountKobo) || amountKobo <= 0) {
      setStatus(STATUS_ID, { message: 'Enter a valid amount.', kind: 'error' });
      return;
    }
    try {
      const res = await api('/wallet/withdraw', {
        method: 'POST',
        body: {
          amount_kobo: amountKobo,
          bank_account_id: data.bank_account_id,
          idempotency_key: idempotencyKey,
        },
      });
      const niceStatus = res.status === 'pending'
        ? 'Withdrawal requested. You\'ll receive the funds within 30 minutes.'
        : res.status === 'succeeded'
          ? 'Withdrawal complete.'
          : `Withdrawal ${res.status}.`;
      const kind = res.status === 'failed' ? 'error' : 'success';
      setStatus(STATUS_ID, { message: niceStatus, kind });
      if (kind === 'success') toast.success(niceStatus);
      setTimeout(() => navigate('/wallet'), 1600);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not start withdrawal.';
      setStatus(STATUS_ID, { message: msg, kind: 'error' });
    }
  });
}

function bankRow(b, selected) {
  return `
    <div class="card" data-bank-id="${escape(b.id)}" style="padding:13px;cursor:pointer;">
      <div class="row">
        <div class="ic teal">${icon(I.bank, { size: 16 })}</div>
        <div class="grow">
          <b style="font-size:13px;color:#fff;font-weight:600;">
            ${escape(b.bank_name)} · ••• ${escape(b.account_number.slice(-4))}
          </b>
          <div style="font-size:11px;color:var(--mute);font-weight:500;">
            ${escape(b.account_name)} · Verified
          </div>
        </div>
        <span class="radio ${selected ? 'on' : ''}"></span>
      </div>
    </div>
  `;
}
